/**
 * FLAGS de «GRATIS» (B5 · gap analysis §N, acciones `N3`–`N5`).
 *
 * Dos interruptores, y ninguno de los dos inventa un mecanismo de acceso nuevo.
 *
 * **Lo gratuito ya está modelado**: es la AUSENCIA de obligación de pago (no hay
 * `price_plan` en la cadena curso→programa→cohorte), que es exactamente lo que
 * la titularidad de B1 llama `has_obligation = false`. Un programa, una cohorte
 * o una clase suelta sin plan de precio son gratis sin que nadie los marque, y
 * por eso aquí NO hay una flag «modo gratis»: no habría nada que encender.
 *
 * Lo que sí necesita interruptor es lo que ABRE contenido o ESCRIBE en el CRM:
 *
 * · `academy.free_preview` — abrir una lección de un curso DE PAGO al mundo. Es
 *   la única pieza de B5 que amplía quién ve qué, así que nace apagada y su gate
 *   está en la ESCRITURA del `access_tier`, no en la lectura: el stream
 *   `free_preview` sirve las muestras ya marcadas igual que el catálogo público
 *   sirve los cursos publicados. Recortar el stream por feature rompería el
 *   local-first sin ganar seguridad (doctrina de `PLATFORM_CONTRACTS` §7).
 *
 * · `academy.free_funnel` — que el sistema anote un lead cuando alguien se
 *   registra o se inscribe a algo gratis. Es escritura automática sobre datos de
 *   otra persona en el CRM: apagada por defecto, y `requires: crm.leads` porque
 *   sin la vertical de captación el lead no tendría dónde vivir.
 */

import type { FeatureManifest } from "../country/packs";

const FREE_PREVIEW: FeatureManifest = {
  key: "academy.free_preview",
  name: "Clase muestra",
  description:
    "Permite marcar lecciones o módulos de un curso de pago como muestra gratuita. La muestra se replica a cualquier visitante autenticado —es material de marketing—, el resto del curso no.",
  scope: "org",
  status: "beta",
  gateable: true,
  // Nace APAGADA: es la única pieza de B5 que AMPLÍA quién ve qué.
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "cursos",
  requires: [],
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Marcar una lección o un módulo como clase muestra",
      "Publicar la muestra gratuita de un curso de pago",
    ],
    // Apagarla no borra nada: las lecciones ya marcadas conservan su
    // `access_tier`. Lo que se impide es marcar más.
    dataPolicy: "preserved",
  },
};

const FREE_FUNNEL: FeatureManifest = {
  key: "academy.free_funnel",
  name: "Funnel automático de gratis a matrícula",
  description:
    "El registro abierto y la inscripción a algo gratuito crean un lead en el CRM con su origen, y la primera factura del alumno lo marca como convertido. Sirve para medir cuánta gente entró por el gancho gratis y cuánta acabó pagando.",
  scope: "org",
  status: "beta",
  gateable: true,
  // Nace APAGADA: escribe datos de personas en el CRM sin que nadie lo pida.
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "crm",
  // Arista DURA: un lead sin la vertical de captación no tiene dónde vivir ni
  // quién lo trabaje.
  requires: ["crm.leads"],
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Crear un lead automático cuando alguien se registra en abierto",
      "Crear un lead automático cuando alguien se inscribe a algo gratuito",
    ],
    dataPolicy: "preserved",
  },
};

/** Manifests de «gratis» (fuente `free_tier`). */
export function freeTierManifests(): FeatureManifest[] {
  return [FREE_PREVIEW, FREE_FUNNEL];
}

/** Keys greppables desde el cliente (espejo de `platform::features::keys`). */
export const FLAG_FREE_PREVIEW = FREE_PREVIEW.key;
export const FLAG_FREE_FUNNEL = FREE_FUNNEL.key;

/** Vocabulario de `access_tier` (espejo del CHECK de la migración 00090). */
export const ACCESS_TIERS = ["paid", "free_preview"] as const;
export type AccessTier = (typeof ACCESS_TIERS)[number];

/** ¿Es esta lección/módulo una clase muestra? */
export function isFreePreview(accessTier: string | null | undefined): boolean {
  return accessTier === "free_preview";
}
